'use client';

import { FC } from 'react';
import Select, {
  components,
  CSSObjectWithLabel,
  type DropdownIndicatorProps,
  type OptionProps,
  Props
} from 'react-select';
import makeAnimated from 'react-select/animated';
import { ReactComponent as Check } from '@assets/icons/check.svg';
import { ReactComponent as ChevronDown } from '@assets/icons/chevron-down.svg';
import { FormikErrors } from 'formik';

import {
  FormControl,
  FormLabel,
  SystemStyleObject,
  useTheme
} from '@chakra-ui/react';

import { ItemInterface, ReactSelectItemInterface } from 'types';

// import { CustomScrollBar } from 'components/CustomScrollBar';

const animatedComponents = makeAnimated();

// const convertToPx = (value: string | number): string =>
//   typeof value === 'number' ? `${value}px` : value;

const DropdownIndicator: FC<DropdownIndicatorProps> = (props) => (
  <components.DropdownIndicator {...props}>
    <ChevronDown width={29} height={29} />
  </components.DropdownIndicator>
);

const IconOption: FC<OptionProps> = (props) => {
  const { label, isSelected } = props;

  return (
    <components.Option {...props}>
      {label}
      {isSelected && <Check width='20px' height='20px' />}
    </components.Option>
  );
};

interface PropsDataInterface {
  [key: string]: string | number;
}

interface SelectFieldProps extends Props {
  name: string;
  value: number | string | null;
  options: ReactSelectItemInterface[] | ItemInterface[];
  error?:
    | string
    | string[]
    | FormikErrors<Record<string, unknown>>
    | FormikErrors<Record<string, unknown>>[];
  touched: boolean;
  placeholder?: string;
  label?: string;
  width?: string | PropsDataInterface;
  menuBackground?: string;
  setFieldValue: (
    field: string,
    value: string,
    shouldValidate?: boolean
  ) => void;
  setFieldTouched: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean
  ) => void;
  bordered?: boolean;
  validateField: (name: string) => void;
  stylesControl?: PropsDataInterface;
  stylesMenu?: PropsDataInterface;
  stylesPlaceholder?: PropsDataInterface;
  stylesMenuList?: PropsDataInterface;
  stylesOption?: {
    [key: string]: string | number | PropsDataInterface;
  };
  stylesValueContainer?: PropsDataInterface;
  dropdownIndicatorStyles?: {
    [key: string]: string | number | PropsDataInterface;
  };
  formControlProps?: PropsDataInterface | Record<string, SystemStyleObject>;
}

const SelectField = ({
  value,
  name,
  options,
  placeholder,
  error,
  touched,
  setFieldValue,
  validateField,
  setFieldTouched,
  label,
  width = '100%',
  bordered,
  stylesControl = {},
  stylesMenu = {},
  stylesPlaceholder = {},
  stylesOption = {},
  stylesValueContainer = {},
  dropdownIndicatorStyles = {},
  formControlProps = {},
  stylesMenuList = {},
  ...rest
}: SelectFieldProps) => {
  const { colors } = useTheme();
  const isErrorExist = error && touched;
  const stateBorderColor = isErrorExist ? colors.red['500'] : 'transparent';
  const stateBgColor = isErrorExist ? colors.redOpacity['5'] : 'white';

  const findItemByValue = (searchedValue: number | string | null) => {
    if (
      searchedValue === null ||
      searchedValue === undefined ||
      searchedValue === ''
    ) {
      return null;
    }
    return (
      // @ts-ignore caused by old ts version
      (options ?? [])?.find(
        (item: ReactSelectItemInterface | ItemInterface) =>
          item.value === searchedValue
      ) || null
    );
  };

  const handleChange = async (selectedItem: ReactSelectItemInterface) => {
    await setFieldValue(name, selectedItem.value);
    validateField(name);
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
  };

  return (
    <FormControl
      position='relative'
      isInvalid={!!isErrorExist}
      width={width}
      {...formControlProps}
    >
      {label && (
        <FormLabel
          fontSize='11px'
          color='gray.375'
          className='label'
          textTransform='uppercase'
          fontFamily='main'
          mb='5px'
          mr={0}
          minHeight='14px'
        >
          {label}
        </FormLabel>
      )}

      <Select
        {...rest}
        value={findItemByValue(value)}
        name={name}
        components={{
          ...animatedComponents,
          DropdownIndicator,
          Option: IconOption
          // TODO: Move MenuList component definition outside of SelectField component to avoid defining components during render.
          /* eslint-disable react/no-unstable-nested-components */
          // MenuList: ({ children, ...props }) => (
          //   <components.MenuList {...props}>
          //     <CustomScrollBar
          //       maxHeight={convertToPx(stylesMenuList?.maxHeight || '150px')}
          //     >
          //       {children}
          //     </CustomScrollBar>
          //   </components.MenuList>
          // )
        }}
        options={options}
        placeholder={placeholder}
        onChange={(option) => handleChange(option as ReactSelectItemInterface)}
        onBlur={handleBlur}
        blurInputOnSelect
        styles={{
          control: (styles, state) =>
            ({
              ...styles,
              background: stateBgColor,
              fontSize: 15,
              color: colors.gray['800'],
              borderRadius: bordered ? 3 : 0,
              borderColor: stateBorderColor,
              borderBottomLeftRadius: state.menuIsOpen || !bordered ? 0 : 3,
              borderBottomRightRadius: state.menuIsOpen || !bordered ? 0 : 3,
              boxShadow: 'none',
              '&:hover': {
                cursor: 'pointer'
              },
              ...stylesControl
            }) as CSSObjectWithLabel,
          indicatorSeparator: (styles) =>
            ({
              ...styles,
              display: 'none'
            }) as CSSObjectWithLabel,
          dropdownIndicator: (styles, state) =>
            ({
              ...styles,
              padding: 0,
              marginRight: '11px',
              fill: colors.gray[250],
              transition: 'all .2s ease',
              transform: state.selectProps.menuIsOpen
                ? 'rotate(180deg)'
                : 'rotate(0)',
              ...dropdownIndicatorStyles
            }) as CSSObjectWithLabel,
          option: (styles) =>
            ({
              ...styles,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '15px',
              lineHeight: '20px',
              background: 'transparent',
              color: colors.gray[800],
              cursor: 'pointer',
              '&:last-child': {
                borderBottom: 'none'
              },
              '&:active': {
                background: 'transparent'
              },
              borderRadius: 0,
              border: 'none',
              padding: '8px 16px',
              '&:hover': {
                background: colors.primary[200]
              },
              ...stylesOption
            }) as CSSObjectWithLabel,
          placeholder: (styles) =>
            ({
              ...styles,
              whiteSpace: 'nowrap',
              ...stylesPlaceholder
            }) as CSSObjectWithLabel,
          menu: (styles) =>
            ({
              ...styles,
              margin: 0,
              boxShadow: 'none',
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              borderBottomLeftRadius: 3,
              borderBottomRightRadius: 3,
              backgroundColor: colors.white,
              ...stylesMenu
            }) as CSSObjectWithLabel,
          menuList: (styles) =>
            ({
              ...styles,
              borderBottomLeftRadius: 3,
              borderBottomRightRadius: 3,
              padding: 0,
              margin: 0,
              boxSizing: 'border-box',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              maxHeight: '200px',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              ...stylesMenuList
            }) as CSSObjectWithLabel,
          valueContainer: (styles) =>
            ({
              ...styles,
              ...stylesValueContainer
            }) as CSSObjectWithLabel
        }}
      />
    </FormControl>
  );
};

export default SelectField;
